import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { BaseParams, PagePathname, RelativePathname } from 'utils/hooks/navigation/types';
import { logger } from 'utils/logger';
import queryString from 'query-string';

export function useRelativeNavigation<
  Params extends { [K in keyof Params]?: string } = {},
  LocationState extends { [K in keyof LocationState]?: unknown } = {}
>(): UseNavigationOutput<null, Params, LocationState> {
  return useNavigation<null, Params, LocationState>(null);
}

export function useAbsoluteNavigation<
  Params extends { [K in keyof Params]?: string } = {},
  LocationState extends { [K in keyof LocationState]?: unknown } = {}
>(): UseNavigationOutput<'', Params, LocationState>;

export function useAbsoluteNavigation<
  Params extends { [K in keyof Params]?: string } = {},
  LocationState extends { [K in keyof LocationState]?: unknown } = {}
>(path: PagePathname): UseNavigationOutput<PagePathname, Params, LocationState>;

export function useAbsoluteNavigation(path?: unknown) {
  return useNavigation(path ?? ('' as unknown as any));
}

/**
 * useNavigation - can work in 2 modes
 * absolute - (default) Suitable if you want to access a specific page from another page.
 * relative - if absoluteRootPathname is set to null. Suitable for navigation inside the page.
 *            Will work even when parent URL change as it creates sub-routes based on current URL
 * @param absoluteRootPathname (string) - URL without tenant code.
 */
function useNavigation<
  RootPath extends AbsoluteRootPathnameWithoutTenant | null,
  Params extends { [K in keyof Params]?: string },
  LocationState extends { [K in keyof LocationState]?: unknown }
>(absoluteRootPathname: RootPath): UseNavigationOutput<RootPath, Params, LocationState> {
  const { location, goBack, push } = useHistory<LocationState>();
  const tenantCode = useTenantCode();
  const routeMatch = useRouteMatch<Params & BaseParams>();
  const urlPrefix = absoluteRootPathname !== null ? `/${tenantCode}${absoluteRootPathname}` : routeMatch.url;

  const goTo = useCallback(
    (pathname: string, options: NavigationOptions<LocationState> = {}) => {
      let path = pathname;
      if (path.includes(tenantCode)) {
        logger.warn('Tenant code is present in target pathname. Please remove it');
        path = path.replace(`/${tenantCode}`, '');
      }
      push({
        pathname: isRelativePathname(path) ? relativePathnameToAbsolute(path, urlPrefix) : `${urlPrefix}${path}`,
        search: options.search,
        state: options.state
      });
    },
    [push, tenantCode, urlPrefix]
  );

  const goToParent = useCallback(
    (upLevel: 1 | 2 = 1, { pathname, ...options }: GoToParentNavigationOptions<LocationState> = {}) => {
      goTo(upLevel === 1 ? `..${pathname ? `/${pathname}` : ``}` : `../..${pathname ? `/${pathname}` : ``}`, options);
    },
    [goTo]
  );

  const goToPage = useCallback(
    (pathname: PagePathname | '', options: NavigationOptions<LocationState> = {}) => {
      goTo(pathname, options);
    },
    [goTo]
  );

  const params = useMemo(() => ({ ...(routeMatch.params ?? {}), tenantCode }), [routeMatch.params, tenantCode]);
  const searchToObject = useCallback(() => {
    if (!location.search) {
      return undefined;
    }
    const result: Partial<any> | undefined = {};
    Object.entries(queryString.parse(location.search, { parseBooleans: true, arrayFormat: 'bracket' })).forEach(
      ([key, value]) => {
        try {
          if (Array.isArray(value)) {
            result[key] = value.map((it) => parse(it));
          } else {
            result[key] = parse(`${value}`);
          }
        } catch (e) {
          result[key] = value;
        }
      }
    );
    return result;
  }, [location.search]);

  return {
    params,
    routePath: absoluteRootPathname === null ? routeMatch.path : `/:tenantCode${absoluteRootPathname}`,
    search: location.search,
    absoluteRouteUrl: urlPrefix as PagePathname,
    routeUrl: routeMatch.url as PagePathname,
    routeUrlWithoutTenant: routeMatch.url.replace(`/${tenantCode}`, '') as PagePathname,
    pathname: location.pathname as PagePathname,
    state: location.state,
    goBack,
    goToPage,
    goToParent,
    searchToObject
  };
}

export function isRelativePathname(pathname: RelativePathname | string): pathname is RelativePathname {
  return pathname.includes('..') || pathname.includes('../..');
}

export function relativePathnameToAbsolute(pathname: RelativePathname, url: string) {
  const path = url.split('/').filter((it) => !!it);
  const pathnameSplit = pathname.split('/');
  const countLevels = pathnameSplit.filter((it) => it === '..').length;
  const restPart = pathnameSplit.filter((it) => it !== '..');
  return `/${path.slice(0, path.length - countLevels).join('/')}${restPart?.length ? `/${restPart.join('/')}` : ''}`;
}

/**
 * AbsoluteRootPathname
 * Passes a string with absolute root path name as the basis for constructing a URL on it instead of constructing a URL based on current path name.
 * An empty string "" resolves to /tenantCode
 * A string "/page" resolves to /tenantCode/page
 */
export type AbsoluteRootPathnameWithoutTenant = '' | PagePathname;

interface UseNavigationOutput<RootPath, Params, LocationState> {
  goToParent(upLevel?: 1 | 2, options?: GoToParentNavigationOptions<LocationState>): void;
  goToPage(url: RootPath extends '' ? PagePathname | '' : string, options?: NavigationOptions<LocationState>): void;
  goBack(): void;
  params: Params & BaseParams;
  state: LocationState | undefined;
  search: string;
  searchToObject: <T extends {}>() => Partial<T> | undefined;
  /**
   * @routerPath
   * returns current Component Route Path (depends on the position of a component in the routing hierarchy) or an AbsoluteRootPathname if given to build a path for the <Route /> component.
   * e.g. full url: /gms/some-path/route-where-is-my-component/deep-nested gives: /:tenantCode/some-path/route-where-is-my-component
   */
  routePath: CurrentPathForRoute;
  /**
   * @absoluteRouteUrl
   * outputs absolute URL based on the AbsoluteRootPathname passed to the hook or current URL if null was passed.
   */
  absoluteRouteUrl: PagePathname;
  /**
   * @routeUrl
   * outputs current Component Route URL (depends on the position of a component in the routing hierarchy). Use it to create a link to your page.
   * e.g. full url: /gms/some-path/route-where-is-placed-my-component/deep-nested gives: /gms/some-path/route-where-is-placed-my-component
   */
  routeUrl: PagePathname;
  routeUrlWithoutTenant: PagePathname;
  /**
   * @pathname
   * returns full URL without search params
   */
  pathname: PagePathname;
}

export interface NavigationOptions<LocationState> {
  state?: LocationState;
  search?: string;
}

export interface GoToParentNavigationOptions<LocationState> extends NavigationOptions<LocationState> {
  pathname?: string;
}

type CurrentPathForRoute = string;

function parse(value: unknown) {
  if (typeof value === 'string' && value[0] === '"') {
    return value.slice(1, -1);
  }
  return JSON.parse(value as string);
}
